import Image from "next/image";

import cn from "classnames";

import { Text } from "@/components/text/text.component";

import type { Theme } from "@/sanity.types";

export type FeatureCardsSectionCardData = {
  id: string;
  title: string;
  description: string;
  imageSrc?: string;
  imageAlt?: string;
  mobileImageSrc?: string;
  mobileImageAlt?: string;
  footnote?: string;
};

export function FeatureCardsSectionCard({
  card,
  theme = "light",
  className,
}: FeatureCardsSectionCardProps) {
  const titleClass = cardTitleClass(theme);
  const bodyClass = cardBodyClass(theme);
  const hasImage = Boolean(card.imageSrc);
  const hasMobileImage = Boolean(card.mobileImageSrc);

  return (
    <article
      data-testid="feature-cards-section-card"
      className={cn(
        "flex h-full flex-col overflow-hidden rounded-2xl",
        cardThemeStyles(theme),
        className,
      )}>
      {hasImage ? (
        <div
          data-testid="feature-cards-section-card-image"
          className="relative aspect-16/10 w-full overflow-hidden">
          {hasMobileImage ? (
            <Image
              src={card.mobileImageSrc!}
              alt={card.mobileImageAlt || card.imageAlt || card.title}
              fill
              className="object-cover md:hidden"
              sizes="100vw"
            />
          ) : null}
          <Image
            src={card.imageSrc!}
            alt={card.imageAlt || card.title}
            fill
            className={
              hasMobileImage ? "hidden object-cover md:block" : "object-cover"
            }
            sizes="(max-width: 767px) 100vw, 33vw"
          />
        </div>
      ) : null}

      <div
        data-testid="feature-cards-section-card-body"
        className="flex flex-1 flex-col gap-3 p-6 md:p-8">
        <Text variant="h5" component="h3" className={titleClass}>
          {card.title}
        </Text>

        <Text variant="body4" component="p" className={bodyClass}>
          {card.description}
        </Text>

        {card.footnote ? (
          <Text
            variant="legal"
            component="p"
            className={cn("mt-auto pt-2", bodyClass)}>
            {card.footnote}
          </Text>
        ) : null}
      </div>
    </article>
  );
}

function usesLightText(theme: Theme | null | undefined) {
  return (
    theme === "secondary" ||
    theme === "secondary100" ||
    theme === "success" ||
    theme === "error" ||
    theme === "promo"
  );
}

function cardTitleClass(theme: Theme | null | undefined) {
  if (usesLightText(theme)) return "text-white";
  if (theme === "tertiary") return "text-secondary";
  return "text-greyscale-headline";
}

function cardBodyClass(theme: Theme | null | undefined) {
  if (usesLightText(theme)) return "text-white/90";
  if (theme === "tertiary") return "text-secondary";
  return "text-greyscale-body";
}

export function cardThemeStyles(theme: Theme | null | undefined) {
  const value = theme ?? "light";
  return cn({
    "bg-primary text-greyscale-body":
      value === "primary" || value === "recommended",
    "bg-secondary text-white": value === "secondary",
    "bg-secondary-100 text-white": value === "secondary100",
    "bg-primary-100 text-secondary": value === "tertiary",
    "bg-greyscale-background text-greyscale-body": value === "light",
    "bg-white text-greyscale-body": value === "white",
    "bg-success text-white": value === "success" || value === "promo",
    "bg-error text-white": value === "error",
  });
}

type FeatureCardsSectionCardProps = {
  card: FeatureCardsSectionCardData;
  theme?: Theme | null;
  className?: string;
};
